import React, { useState, useEffect, useCallback } from "react";
import { Box, useMediaQuery } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../hooks/sharedHooks";
import { sendMessage } from "../chatBotSlice";
import { POPUP_THREAD_ID } from "../models/chatBotModels";
import ChatPanelHeader from "./ChatPanelHeader";
import ChatMessageList from "./ChatMessageList";
import ChatInputArea from "./ChatInputArea";
import { dt } from "../../themes/redesignTokens";

interface ChatPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const ChatPanel: React.FC<ChatPanelProps> = ({ isOpen, onClose }) => {
  const dispatch = useAppDispatch();
  // MIDAS-SCOPESTREAM: Read isLoading from popup's thread buffer
  const popupBuffer = useAppSelector((state) => state.chatBot.threadBuffers[POPUP_THREAD_ID]);
  const isLoading = popupBuffer?.isLoading || false;
  const machineContext = useAppSelector((state) => state.chatBot.machineContext);
  const isPhone = useMediaQuery("(max-width:768px)");
  const isTablet = useMediaQuery("(min-width:768px) and (max-width:1024px)");

  // Closing animation state
  const [isClosing, setIsClosing] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setIsClosing(false);
    } else if (shouldRender) {
      setIsClosing(true);
      const timer = setTimeout(() => {
        setShouldRender(false);
        setIsClosing(false);
      }, isPhone ? 300 : 150);
      return () => clearTimeout(timer);
    }
  }, [isOpen, isPhone, shouldRender]);

  const handleSend = useCallback(
    (message: string, image?: File) => {
      dispatch(
        sendMessage({
          message,
          machineId: machineContext?.machineId,
          image,
          originThreadId: POPUP_THREAD_ID,
        })
      );
    },
    [dispatch, machineContext]
  );

  const handleSuggestionClick = useCallback(
    (text: string) => {
      handleSend(text);
    },
    [handleSend]
  );

  if (!shouldRender) return null;

  // Determine animation
  const openAnimation = isPhone
    ? "midasPanelSlideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1) both"
    : "midasPanelOpen 0.25s cubic-bezier(0.34, 1.56, 0.64, 1) both";

  const closeAnimation = isPhone
    ? "midasPanelSlideDown 0.3s cubic-bezier(0.4, 0, 0.2, 1) both"
    : "midasPanelClose 0.15s cubic-bezier(0.4, 0, 1, 1) both";

  // Panel dimensions.
  // BUG-MIDAS-MOBILE-0414 (Bug 3): On Samsung Chrome the small MIDAS popover's
  // input field rendered below the visible viewport. Root-cause diagnosis:
  //   1. `height: 100vh` excludes the dynamic browser chrome on Android
  //      (address bar + bottom nav). Actual visible area is smaller, so the
  //      flex column's last child (the input row) landed below the fold.
  //      Fix: use `100dvh` (dynamic viewport height) with a `100vh` fallback
  //      via a CSS string union for older browsers.
  //   2. ChatMessageList has `flex: 1` + `overflowY: auto` but NO `minHeight: 0`
  //      on the parent. In a flex column the default `min-height: auto` lets
  //      children expand to their intrinsic content height — so a long
  //      message list pushed the fixed-height input past the container's
  //      bottom edge, which was then clipped by the panel's `overflow: hidden`.
  //      Fix: add `minHeight: 0` via a wrapper around ChatMessageList so the
  //      scrollable region truly shrinks and the input stays pinned at the
  //      bottom.
  //   3. Samsung One UI gesture nav contributes an `env(safe-area-inset-bottom)`
  //      that was not accounted for — add it to the input container to keep
  //      the send button clear of the gesture handle.
  const panelWidth = isPhone ? "100vw" : isTablet ? "380px" : "420px";
  const panelHeight = isPhone ? "100dvh" : isTablet ? "500px" : "600px";

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: isPhone ? 0 : "32px",
        right: isPhone ? 0 : "36px",
        top: isPhone ? 0 : "auto",
        left: isPhone ? 0 : "auto",
        width: panelWidth,
        // Height: `100vh` fallback for older browsers, then `100dvh` for
        // modern Android Chrome / iOS Safari so the panel fits inside the
        // visible viewport (excluding mobile browser chrome). `maxHeight`
        // also constrained — prevents accidental overflow on very small
        // viewports. `panelHeight` already encodes `100dvh` on phone.
        height: panelHeight,
        maxHeight: isPhone ? "100dvh" : panelHeight,
        borderRadius: isPhone ? 0 : "16px 16px 16px 4px",
        zIndex: 1350,
        boxShadow: isPhone
          ? "none"
          : "0 8px 40px rgba(0, 0, 0, 0.12), 0 2px 12px rgba(0, 0, 0, 0.06)",
        backgroundColor: dt.cardBg,
        border: isPhone ? "none" : `1px solid ${dt.cardBorder}`,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        transformOrigin: "bottom right",
        animation: isClosing ? closeAnimation : openAnimation,
      }}
    >
      <ChatPanelHeader onClose={onClose} />
      {/* Scrollable region wrapper — `minHeight: 0` lets ChatMessageList's
          inner `flex: 1 + overflow: auto` actually shrink below its content
          height, so the input row stays visible at the bottom (Bug 3 fix). */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <ChatMessageList
          onSuggestionClick={handleSuggestionClick}
          isPhone={isPhone}
          threadId={POPUP_THREAD_ID}
        />
      </Box>
      {/* Input container — respect Samsung gesture-nav safe area on phone. */}
      <Box
        sx={{
          flexShrink: 0,
          paddingBottom: isPhone ? "env(safe-area-inset-bottom)" : 0,
        }}
      >
        <ChatInputArea onSend={handleSend} disabled={isLoading} />
      </Box>
    </Box>
  );
};

export default ChatPanel;
